/**
 * Readable messages for failed API requests (F16, W1 sweep 2026-10-01).
 *
 * `createFetcher` (@plerity/data-access `normalizeError`) rejects with a plain
 * `{ code, message, details, status, responseData }` object — not an axios
 * error — so `err.response.data` is never there. The Plerity envelope's `error`
 * is already on `message`; a FastAPI `HTTPException` body (`{ detail }`, a 422
 * `detail: [{ msg }]` list or a `detail: { message }` object) only survives on
 * `responseData`. Raw axios errors (`response.data`) are still accepted for
 * callers that use axios directly.
 *
 * Transport noise ("Request failed with status code 409", "Internal Server
 * Error", HTML error pages) and programming errors (TypeError & co.) never
 * reach the user — the caller's fallback is shown instead.
 */

type ErrorLike = {
  code?: unknown;
  message?: unknown;
  status?: unknown;
  responseData?: unknown;
  response?: { status?: unknown; data?: unknown };
} | null;

const GENERIC_MESSAGE =
  /^(request failed with status code \d+|network error|internal server error|bad gateway|service unavailable|gateway timeout|bad request|not found|unprocessable (entity|content)|http_\d+|api returned \d+|canceled|unknown)$/i;

const MAX_MESSAGE_LENGTH = 300;

function readable(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text || text.length > MAX_MESSAGE_LENGTH || text.includes("<") || GENERIC_MESSAGE.test(text)) return null;
  return text;
}

/** First readable reason in a FastAPI 422 `detail` list (`[{ loc, msg, type }]`). */
function fromValidationList(list: unknown[]): string | null {
  for (const item of list) {
    const msg = readable(typeof item === "string" ? item : (item as { msg?: unknown } | null)?.msg);
    // Pydantic prefixes custom validator messages with "Value error, ".
    if (msg) return msg.replace(/^value error,\s*/i, "");
  }
  return null;
}

function fromBody(body: unknown): string | null {
  if (typeof body === "string") return readable(body);
  if (!body || typeof body !== "object") return null;
  const b = body as { detail?: unknown; error?: unknown; message?: unknown; msg?: unknown };
  for (const field of [b.detail, b.error, b.message, b.msg]) {
    if (Array.isArray(field)) {
      const msg = fromValidationList(field);
      if (msg) return msg;
      continue;
    }
    if (field && typeof field === "object") {
      const nested = fromBody(field);
      if (nested) return nested;
      continue;
    }
    const msg = readable(field);
    if (msg) return msg;
  }
  return null;
}

const PROGRAMMING_ERRORS = ["TypeError", "ReferenceError", "SyntaxError", "RangeError"];

/** The API's own reason for a failure when it sent one, else `fallback`. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (err == null) return fallback;
  if (typeof err === "string") return readable(err) ?? fallback;
  const e = err as ErrorLike;
  const fromApi = fromBody(e?.responseData ?? e?.response?.data);
  if (fromApi) return fromApi;
  if (err instanceof Error && PROGRAMMING_ERRORS.includes(err.name)) return fallback;
  return readable(e?.message) ?? fallback;
}

/** HTTP status of a failed request (normalized fetcher error or raw axios error). */
export function apiErrorStatus(err: unknown): number | undefined {
  const e = err as ErrorLike;
  const status = e?.status ?? e?.response?.status;
  return typeof status === "number" ? status : undefined;
}
